import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import Icon from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import TextField from "@insite/mobius/TextField";
import getColor from "@insite/mobius/utilities/getColor";
import { CustomProductFilters } from "@tSurya/Handlers/ProductList/AddProductAvailibility";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import groupBy from "lodash/groupBy";
import values from "lodash/values";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    quantityButton?: ButtonPresentationProps;
    clearButton?: ButtonPresentationProps;
    container?: GridContainerProps;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
            `,
        },
        css: css`
            .group-header {
                width: 100%;
                display: flex;
                align-items: center;
                cursor: pointer;
                margin-bottom: 5px;
                .pulse {
                    display: inline-block;
                    width: 8px;
                    height: 8px;
                    margin-left: 5px;
                    border-radius: 50%;
                    background-color: #ff3366;
                    animation: pulse 2s infinite;
                    .span {
                        vertical-align: text-top;
                    }
                }
            }
            @keyframes pulse {
                0%,
                100% {
                    transform: scale(1);
                    opacity: 0.8;
                }
                50% {
                    transform: scale(1.2);
                    opacity: 0.5;
                }
            }

            .expanded .pulse {
                display: none;
            }
        `,
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            &:not(:last-child) {
                padding-bottom: 5px;
            }
            &.subChildCheckBox {
                padding-left: 15px;
                padding-bottom: 0 !important;
                display: inline-block;
                legend {
                    margin-bottom: 5px !important;
                }
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
            weight: "bold",
        },
    },
    seeAllLink: {
        css: css`
            padding-top: 15px;
            > span {
                font-size: ${TYPOGRAPHY.size.small};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                font-weight: ${TYPOGRAPHY.weight.bold};
                color: ${getColor("text.link")};
                font-family: ${TYPOGRAPHY.fontFamily.title};
            }
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 15px;
            > span {
                font-size: ${TYPOGRAPHY.size.small};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                font-weight: ${TYPOGRAPHY.weight.bold};
                color: ${getColor("text.link")};
                font-family: ${TYPOGRAPHY.fontFamily.title};
            }
        `,
    },
    quantityButton: {
        css: css`
            margin-right: 20px;
            margin-left: 10px;
        `,
    },
    clearButton: {
        css: css`
            border: 2px solid ${getColor("primary")};
            color: ${getColor("text.main")};
            :hover {
                border: 2px solid ${getColor("primary")} !important;
                color: ${getColor("text.main")} !important;
            }
        `,
    },
    container: {
        css: css`
            overflow-y: auto;
            overflow-x: hidden;
            max-height: 200px;
        `,
    },
};

export interface OwnProps {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
    titleKeyName: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const productFiltersUpdated: CustomProductFilters = state.pages.productList.productFilters;
    return {
        selectedFilter: state.pages.productList.productFilters.attributeValueIds,
        minimumPrice: productFiltersUpdated.minimumPrice?.split(".")[1],
        minimumQty: productFiltersUpdated.minimumPrice?.split(".")[0],

    };
};

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    titleKeyName,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
    addProductFilters,
    removeProductFilters,
    selectedFilter,
    minimumPrice,
    minimumQty,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);
    const [value, setValue] = useState<string>("");
    const [quantity, setQuantity] = useState<string>("");
    const [error, setError] = useState<boolean>(false);
    const [inStock, setInStock] = useState<string>("");
    const [selectedFacet, setSelectedFacet] = useState<FacetModel | null>();
    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const anySelected = facets.find(f => f.selected) !== undefined;
    const groupByObj = groupBy(facets, item => item.name.split("|")[0]);
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
    const [searchFilter, setSearchFilter] = useState("");
    const [clearValue, setClearValue] = useState(false);
    const [isQuantityChanged, setIsQuantityChanged] = useState(false);

    useEffect(() => {
        minimumPrice !== undefined ? setInStock(minimumPrice) : setInStock("");
        minimumQty !== undefined ? setQuantity(minimumQty) : setQuantity("");
        if (clearValue) {
            setQuantity("");
            setClearValue(false);
        }

    }, [minimumPrice]);

    const inputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inpValue = e.target.value;
        setValue(inpValue);
        const sortedValue = limitedFacets.filter(facet => facet.name === inpValue);
        if (sortedValue.length > 0) {
            onChangeFacet(sortedValue[0]);
            setSelectedFacet(sortedValue[0]);
        }
        if (value === "" && selectedFacet) {
            selectedFacet.selected = true;
            onChangeFacet(selectedFacet);
            setSelectedFacet(null);
        }
    };

    const quantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setError(false);
        setQuantity(e.target.value);
        if (validateQuantityChange(quantity)) {
            addProductFilters({
                minimumPrice: `${(e.target.value === "0" ? 1 : e.target.value) || 1}.${
                    inStock === "" || undefined ? "00" : inStock
                }`,
                page: 1,
            } as CustomProductFilters);
        }  
    };

    const validateQuantityChange = (value: string) => {
        const field = Number(value);
        if (field && !Number.isInteger(field)) {
            setQuantity("");
            setError(true);
            return false;
        }
        return true;
    };

    const checkStock = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInStock(event.currentTarget.value);
        if (validateQuantityChange(quantity)) {
            addProductFilters({
                minimumPrice: `${(quantity === "0" ? 1 : quantity) || 1}.${
                    event.currentTarget.value === "" || undefined ? "00" : event.currentTarget.value
                }`,
                page: 1,
            } as CustomProductFilters);
            setError(false);
        }    
        if (minimumPrice === undefined) {
            setClearValue(true);
        }
    };

    const searchStock = () => {
        if (validateQuantityChange(quantity)) {
            addProductFilters({
                minimumPrice: `${(quantity === "0" ? 1 : quantity) || 1}.${
                    inStock === "" || undefined ? "00" : inStock
                }`,
                page: 1,
            } as CustomProductFilters);
            setError(false);
        }
    };

    const clearSearch = () => {
        removeProductFilters({
            minimumPrice: `${quantity || 0}.${inStock}`,
        } as CustomProductFilters);
        setQuantity("");
        setInStock("");
        setError(false);
    };
    const onChangeAllFacet = (group: string) => {
        const selectedItems = groupByObj[group].map(item => item.id);
        if (selectedGroups.includes(group)) {
            setSelectedGroups(selectedGroups.filter(grp => grp !== group));
            selectedItems.filter(item => item.split("|")[0] !== group);
            removeProductFilters({ attributeValueIds: selectedItems });
        } else {
            // setSelectedGroups([...selectedGroups, group]);
            addProductFilters({ attributeValueIds: selectedItems });
        }
    };
    const toggleGroupExpansion = (group: string) => {
        setExpandedGroups(prevExpandedGroups =>
            prevExpandedGroups.includes(group)
                ? prevExpandedGroups.filter(grp => grp !== group)
                : [...prevExpandedGroups, group],
        );
    };
    useEffect(() => {
        if (selectedFilter && selectedFilter?.length > 0) {
            const newSelectedGroups = Object.keys(groupByObj).filter(group =>
                groupByObj[group].every(facet => selectedFilter.includes(facet.id)),
            );
            setSelectedGroups(newSelectedGroups);
        } else {
            setSelectedGroups([]);
        }
    }, [selectedFilter]);

    return (
        <ManagedAccordionSection
            title={title}
            {...styles.accordionSection}
            initialExpanded={expandByDefault || anySelected}
        >
            {title === translate("Product Availability") ? (
                <StyledWrapper>
                    <TextField
                        type="number"
                        value={quantity}
                        onChange={quantityChange}
                        error={error}
                        // onBlur={() => validateQuantityChange(quantity)}
                        placeholder="Enter Qty (optional)"
                        min={0}
                    />
                    <RadioGroup value={inStock} onChangeHandler={checkStock}>
                        <Radio value="00">Available Today</Radio>
                        <Radio value="30">Available Within 30 Days</Radio>
                        <Radio value="60">Available Within 60 Days</Radio>
                        <Radio value="90">Available Over 60 Days</Radio>
                    </RadioGroup>
                    {/* <Button variant="primary" {...styles.quantityButton} onClick={() => searchStock()}>
                        Search
                    </Button>
                    <Button variant="secondary" {...styles.clearButton} onClick={() => clearSearch()}>
                        Clear
                    </Button> */}
                </StyledWrapper>
            ) : title === "Available Quantity" ? (
                <TextField type="number" onBlur={inputChange} />
            ) : titleKeyName === "CompoundItemShapeSizeGroup" ? (
                Object.entries(groupByObj).map(([group, facetitems]) => (
                    <>
                        <StyledWrapper
                            css={css`
                                display: flex;
                                justify-content: space-between;
                                margin-right: 21px;
                            `}
                        >
                            
                            <StyledWrapper  
                            onClick={() => toggleGroupExpansion(group)}
                            css={css`
                                display: flex;
                                justify-content: space-between;
                                cursor: pointer;
                            `} >{group}
                            </StyledWrapper>
                            <StyledWrapper
                                onClick={() => toggleGroupExpansion(group)}
                                className={`group-header ${expandedGroups.includes(group) ? "expanded" : ""}`}
                            >
                                <span className="pulse">
                                    {expandedGroups.includes(group) ? (
                                        <Icon src={Minus} size={14} />
                                    ) : (
                                        <Icon src={Plus} size={14} />
                                    )}
                                </span>
                            </StyledWrapper>
                        </StyledWrapper>
                        {expandedGroups.includes(group) &&
                            facetitems.map(facet => (
                                <CheckboxGroup
                                    key={facet.id}
                                    data-test-selector={group}
                                    {...styles.checkBoxGroup}
                                    className="subChildCheckBox"
                                >
                                    <Checkbox
                                        checked={facet.selected}
                                        onChange={() => onChangeFacet(facet)}
                                        data-test-selector={`facetFilter${facet.id}`}
                                    >
                                        {facet.name.split("|")[1]}{" "}
                                        {facet.count !== -1 &&
                                            `(${String(facet.count).replace(/\B(?=(\d{3})+(?!\d))/g, ",")})`}
                                    </Checkbox>
                                </CheckboxGroup>
                            ))}
                        <StyledWrapper
                            css={css`
                                margin-bottom: 5px;
                            `}
                        ></StyledWrapper>
                    </>
                ))
            ) : title === "Collection" ? (
                <>
                    <TextField
                        id="searchFilter"
                        onChange={event => setSearchFilter(event.target.value)}
                        value={searchFilter}
                        placeholder={translate("Search")}
                        css={css`
                            width: 90%;
                            margin-bottom: 10px;
                        `}
                    />

                    <GridContainer {...styles.container}>
                        {facets
                            ?.filter(
                                facet =>
                                    facet.name?.toLowerCase().includes(searchFilter.toLowerCase()) ||
                                    searchFilter === "",
                            )
                            .map(facet => (
                                <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                                    <Checkbox
                                        {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                        checked={facet.selected}
                                        onChange={() => onChangeFacet(facet)}
                                        data-test-selector={`facetFilter${facet.id}`}
                                    >
                                        {facet.name}{" "}
                                        {facet.count !== -1 &&
                                            `(${String(facet.count).replace(/\B(?=(\d{3})+(?!\d))/g, ",")})`}
                                    </Checkbox>
                                </CheckboxGroup>
                            ))}
                    </GridContainer>
                </>
            ) : (
                <GridContainer>
                    {limitedFacets?.map(facet => (
                        <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                            <Checkbox
                                {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                checked={facet.selected}
                                onChange={() => onChangeFacet(facet)}
                                data-test-selector={`facetFilter${facet.id}`}
                            >
                                {facet.name}{" "}
                                {facet.count !== -1 && `(${String(facet.count).replace(/\B(?=(\d{3})+(?!\d))/g, ",")})`}
                            </Checkbox>
                        </CheckboxGroup>
                    ))}
                </GridContainer>
            )}
            {facets &&
                !(titleKeyName === "CompoundItemShapeSizeGroup") &&
                title !== "Collection" &&
                facets.length > showMoreLimit &&
                (expanded ? (
                    <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                        {translate("See Less")}
                    </Link>
                ) : (
                    <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                        {translate("See All")}
                    </Link>
                ))}
        </ManagedAccordionSection>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductListFiltersAccordionSection);
